import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import Header from "@/components/Header";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Spinner } from "@phosphor-icons/react";

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 60000;

export const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [jobId, setJobId] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    const started = Date.now();
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setJobId(data.job_id);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.status === "expired" || data.status === "failed") {
          setStatus("failed");
          return;
        }
        if (Date.now() - started > POLL_TIMEOUT_MS) {
          setStatus("timeout");
          return;
        }
      } catch (e) {
        // Keep polling
      }
      setTimeout(poll, POLL_INTERVAL_MS);
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-white" data-testid="payment-success-page">
      <Header />
      <main className="mx-auto max-w-md px-6 py-16 md:py-24">
        <div className="rounded-md border border-[#E5E2DB] bg-white p-8 text-center">
          {status === "polling" && (
            <>
              <Spinner size={40} weight="duotone" className="mx-auto animate-spin text-[#C65D47]" />
              <h1 className="mt-4 font-display text-2xl font-bold text-[#0A0F0D]">
                Confirming your payment…
              </h1>
              <p className="mt-2 text-sm text-[#5C605E]">
                This usually takes a couple of seconds.
              </p>
            </>
          )}
          {status === "paid" && (
            <>
              <CheckCircle size={48} weight="fill" className="mx-auto text-[#0A0F0D]" />
              <h1 className="mt-4 font-display text-2xl font-bold text-[#0A0F0D]">
                Payment received!
              </h1>
              <p className="mt-2 text-sm text-[#5C605E]">
                Funds are safely in escrow. Your contractor will start work now.
              </p>
              <div className="mt-6 flex flex-col gap-2">
                {jobId && (
                  <Link to={`/jobs/${jobId}`}>
                    <Button
                      data-testid="payment-success-view-job"
                      className="w-full bg-[#0A0F0D] text-white hover:bg-[#C65D47]"
                    >
                      View job
                    </Button>
                  </Link>
                )}
                <Button
                  variant="outline"
                  onClick={() => navigate("/dashboard")}
                  className="w-full"
                >
                  Back to dashboard
                </Button>
              </div>
            </>
          )}
          {(status === "failed" || status === "timeout" || status === "error") && (
            <>
              <XCircle size={48} weight="fill" className="mx-auto text-[#C65D47]" />
              <h1 className="mt-4 font-display text-2xl font-bold text-[#0A0F0D]">
                Payment status unclear
              </h1>
              <p className="mt-2 text-sm text-[#5C605E]">
                Please check your dashboard — if the job is still marked "awaiting payment", try again.
              </p>
              <Button
                onClick={() => navigate("/dashboard")}
                className="mt-4 w-full bg-[#0A0F0D] text-white hover:bg-[#C65D47]"
              >
                Back to dashboard
              </Button>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export const PaymentCancel = () => {
  const [params] = useSearchParams();
  const jobId = params.get("job_id");
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white" data-testid="payment-cancel-page">
      <Header />
      <main className="mx-auto max-w-md px-6 py-16 md:py-24">
        <div className="rounded-md border border-[#E5E2DB] bg-white p-8 text-center">
          <XCircle size={48} weight="fill" className="mx-auto text-[#5C605E]" />
          <h1 className="mt-4 font-display text-2xl font-bold text-[#0A0F0D]">
            Payment cancelled
          </h1>
          <p className="mt-2 text-sm text-[#5C605E]">No charge was made. You can try again anytime.</p>
          <Button
            onClick={() => navigate(jobId ? `/jobs/${jobId}` : "/dashboard")}
            data-testid="payment-cancel-return"
            className="mt-4 w-full bg-[#0A0F0D] text-white hover:bg-[#C65D47]"
          >
            {jobId ? "Return to job" : "Back to dashboard"}
          </Button>
        </div>
      </main>
    </div>
  );
};
